import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getConsent, setConsent } from "@/lib/analytics";

/**
 * Slim, dismissible consent banner shown bottom-left until the user makes a
 * choice. Stays out of the way of the fixed `SiteFooter` on the right.
 * Choice is persisted in localStorage; banner never re-shows for that visitor.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer to next tick so we don't flash during initial render.
    const t = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const accept = () => { setConsent("accepted"); setVisible(false); };
  const decline = () => { setConsent("declined"); setVisible(false); };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      style={{
        position: "fixed",
        left: 16,
        bottom: 56, // sit above the SiteFooter
        zIndex: 55,
        maxWidth: 380,
        background: "var(--v-bg)",
        border: "1px solid var(--v-border)",
        borderLeft: "3px solid var(--v-accent)",
        padding: 18,
        boxShadow: "0 24px 48px -16px rgba(0,0,0,0.45)",
      }}
    >
      <div className="overline" style={{ color: "var(--v-accent)" }}>Analytics cookies</div>
      <p style={{ marginTop: 8, fontSize: 13, color: "var(--v-text)", lineHeight: 1.55 }}>
        Verdict uses Google Analytics to understand how the product is used —
        anonymous page views only. We never share your audit content or login
        data with any analytics provider.
      </p>
      <p style={{ marginTop: 8, fontSize: 11, color: "var(--v-text-2)", lineHeight: 1.55 }}>
        See our <Link to="/privacy" style={{ color: "var(--v-accent)" }}>Privacy Policy</Link> for details.
      </p>
      <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={decline}
          data-testid="cookie-decline-btn"
          className="v-btn v-btn-ghost"
          style={{ padding: "6px 14px", fontSize: 11 }}
        >
          Decline
        </button>
        <button
          type="button"
          onClick={accept}
          data-testid="cookie-accept-btn"
          className="v-btn"
          style={{ padding: "6px 16px", fontSize: 11 }}
        >
          Accept cookies
        </button>
      </div>
    </div>
  );
}
